import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {CmsPagesFacade} from '@kion/data-access/data-access-cms-pages';
import {CmsConfig} from '../../config/cms.config';
import {ActivatedRoute} from '@angular/router';
import {BaseCmsComponent, BaseSite, CmsContentSlot, CmsPage} from '@kion/data-models';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {templateMapping} from '../../config/templateMapping';
import {componentMapping} from '../../config/componentMapping';
import {BaseSitesFacade} from '@kion/data-access/data-access-base-sites';
import {CorrelationObserverService} from '@kion/ngrx-helper';
import {TranslateService} from '@ngx-translate/core';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {ToastService} from '@kion/ui';
import {SubSink} from 'subsink';
import {SeoService} from '../../services/seo.service';

@Component({
  selector: 'kion-cms-base-site',
  templateUrl: './base-site.component.html',
  styleUrls: ['./base-site.component.scss']
})
export class BaseSiteComponent implements OnInit, OnChanges, OnDestroy {

  @Input() pageId: string;
  @Input() isProductPage = false;
  @Input() isSmartEdit = false;
  @Input() productCode: string;

  cmsPage$: Observable<CmsPage>;
  slots$: Observable<string[]>;
  contentSlots$: Observable<CmsContentSlot[]>;

  templateMapping = templateMapping;
  componentMapping = componentMapping;

  searchToggled: boolean;

  readonly project: string;
  step$: Observable<number>;

  private readonly subSink: SubSink;

  showNotAvailablePage: boolean;

  isFullHeight: boolean;

  constructor(
    private cmsConfig: CmsConfig,
    private cmsPagesFacade: CmsPagesFacade,
    private activatedRoute: ActivatedRoute,
    private baseSitesFacade: BaseSitesFacade,
    private correlationObserverService: CorrelationObserverService,
    private translate: TranslateService,
    private toastService: ToastService,
    private seoService: SeoService
  ) {
    this.subSink = new SubSink();
    this.project = this.cmsConfig.project
  }

  loadPage(): void {
    this.cmsPage$ = this.cmsPagesFacade.all$.pipe(
      map(
        (cmsPages: CmsPage[]) => cmsPages.find(
          (cmsPage: CmsPage, index) => {
            if (this.isSmartEdit) {
              if (cmsPage.properties?.smartedit) {
                cmsPage.properties.smartedit.classes.split(' ').forEach(className => {
                  document.body.classList.add(className)
                });
              }
              return index === 0
            }
            return cmsPage.label ? cmsPage.label === this.pageId : cmsPage.uid === this.pageId
          }
        )
      )
    );
    this.slots$ = this.cmsPage$.pipe(
      map(
        (page: CmsPage) => {
          if (page) {
            return page.contentSlots.contentSlot.map((slot: CmsContentSlot) => slot.position);
          }
          return null;
        }
      )
    );
    this.contentSlots$ = this.cmsPage$.pipe(
      map(
        (page: CmsPage) => {
          if (page) {
            return page.contentSlots.contentSlot;
          }
          return null;
        }
      )
    );
    this.subSink.sink = this.cmsPage$.subscribe(
      cmsPage => {
        if (cmsPage) {
          this.seoService.setMetadata(cmsPage, this.subSink);
        }
      }
    );
  }

  ngOnInit(): void {
    this.isFullHeight = this.activatedRoute.snapshot.url.map(tree => tree.path).indexOf(this.cmsConfig.routes?.notfoundBaseRoute) !== -1;
    this.searchToggled = false;
    this.step$ = this.activatedRoute.data.pipe(
      map(
        data => {
          if (data.checkout && data.step){
            return data.step
          }
          return 0;
        }
      )
    );
    this.subSink.sink = this.correlationObserverService.subscribeToAction(
      this.baseSitesFacade.loadAll('load_baseSite_initial'),
      () => {
        this.subSink.sink = this.baseSitesFacade.all$.pipe(take(1)).subscribe(
          (baseSites: BaseSite[]) => this.baseSitesFacade.setActive(
            baseSites.find((baseSite: BaseSite) => baseSite.id === this.cmsConfig.baseSite)
          )
        )
      },
      () => {
        //TODO: translate (local)
        this.toastService.show(
          {
            text: 'could not load baseSite',
            type: 'error'
          }
        );
      }
    );
    if (this.pageId) {
      let action = null;
      if (this.isProductPage) {
        action = this.cmsPagesFacade.loadProductPage(this.productCode, this.productCode)
      } else if (this.pageId === 'cx-preview') {
        const ticketId = this.activatedRoute.snapshot.queryParams.cmsTicketId;
        action = this.cmsPagesFacade.loadPage(this.pageId, true, ticketId, ticketId)
      } else {
        action = this.cmsPagesFacade.loadPage(this.pageId, false, null, this.pageId)
      }
      this.subSink.sink = this.correlationObserverService.subscribeToAction(
        action,
        () => this.loadPage(),
        () => {
          this.cmsPagesFacade.error$.pipe(
            take(1)
          ).subscribe(() => {
            this.pageId = '404';
            this.subSink.sink = this.correlationObserverService.subscribeToAction(
              this.cmsPagesFacade.loadPage(this.pageId, false, null, this.pageId),
              () => {
                this.loadPage();
              },
              () => this.showNotAvailablePage = true
            )
          })
          //TODO: translate (local)
          this.toastService.show(
            {
              text: 'could not load page',
              type: 'error'
            }
          );
        }
      )
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageId) {
      if (!changes.pageId.firstChange) {
        this.ngOnInit();
      }
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  getComponentsBySlot$(position: string): Observable<BaseCmsComponent[]> {
    return this.contentSlots$.pipe(
      map(
        (slots) => slots?.find(slot => slot.position === position)?.components.component
      )
    )
  }

  getSlot$(position: string): Observable<CmsContentSlot> {
    return this.contentSlots$.pipe(
      map(
        (slots) => slots?.find(slot => slot.position === position)
      )
    )
  }


  searchClicked() {
    this.searchToggled = !this.searchToggled;
  }
}
